import axios from "axios";
import queryString from "query-string";

const getBaseUrl = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || "development";
  return env === "production"
    ? process.env.REACT_APP_PROD_BACKEND_URL
    : env === "staging"
      ? process.env.REACT_APP_STAGING_BACKEND_URL
      : process.env.REACT_APP_DEV_BACKEND_URL;
};

const base_url = getBaseUrl();

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only redirect to login if we're not already there and not in the middle
      // of a form submission — prevents a single bad request from wiping the session
      // while the user is actively working.
      const isLoginPage = window.location.pathname === "/login";
      const isAuthEndpoint =
        error.config?.url?.includes("/auth/login") ||
        error.config?.url?.includes("/auth/register");

      const isPublicPage = window.location.pathname.startsWith("/reporting") ||
        window.location.pathname.startsWith("/ai-visibility");

      if (!isLoginPage && !isAuthEndpoint && !isPublicPage) {
        // Give a short delay so any in-flight error handlers can show their
        // message before we navigate away
        setTimeout(() => {
          localStorage.removeItem("accessToken");
          window.location.href = "/login";
        }, 1500);
      }
    }
    return Promise.reject(error);
  },
);

export const postRegistration = (values) => {
  return axios.post(
    `${base_url}/auth/register`,
    { ...values },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const postEmail2FAAuth = (accessToken) => {
  return axios.post(
    `${base_url}/auth/two-fa-verify`,
    { two_fa_type: "email" },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
};

export const postEmail2FAAuthCode = (code, accessToken) => {
  return axios.post(
    `${base_url}/auth/check-two-fa-verify`,
    { code },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
};

export const getStripeProductsPlans = (accessToken) => {
  return axios.get(`${base_url}/account/stripe-product-list`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const postStripeProduct = ({ id }, accessToken) => {
  return axios.post(
    `${base_url}/payment/subscribe`,
    { product_id: id },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postConfirmSubscription = (
  { subscriptionId, selectedPlan },
  accessToken,
) => {
  return axios.post(
    `${base_url}/payment/subscription-confirm`,
    {
      subscription_id: subscriptionId,
      product_id: selectedPlan.stripeId,
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getProjects = (accessToken, query) => {
  return axios.get(
    `${base_url}/projects?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const getDomains = (accessToken, query) => {
  return axios.get(
    `${base_url}/domains?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const postDomains = (accessToken, { name, project_type, status }) => {
  return axios.post(
    `${base_url}/domains`,
    { name, project_type, status },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getDomainById = (accessToken, id) => {
  return axios.get(`${base_url}/domains/${id}/edit`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const putDomains = (accessToken, { name, project_type, status }, id) => {
  return axios.put(
    `${base_url}/domains/${id}`,
    { name, project_type, status },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getCheckConnection = (query, accessToken) => {
  return axios.get(
    `${base_url}/integrations/gsc-check-connection?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const getIntegrations = (accessToken) => {
  return axios.get(`${base_url}/integrations`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const deleteIntegration = (accessToken, id) => {
  return axios.delete(`${base_url}/integrations/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getStats = (accessToken, period) => {
  return axios.get(`${base_url}/integrations/gsc-stat?period=${period}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getSearchTutorials = (accessToken, query) => {
  return axios.get(
    `${base_url}/tutorials?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const postSeedPageFile = (accessToken, { file, type }) => {
  return axios.post(
    `${base_url}/upload`,
    { file, type: type.toLowerCase() },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const deletePrimaryImage = (accessToken, url, type) => {
  return axios.delete(`${base_url}/images/delete-upload`, {
    data: { url, type },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postPrimaryImage = (accessToken, { file, type }) => {
  return axios.post(
    `${base_url}/images/upload-primary`,
    { file, type: type.toLowerCase() },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getDomainsSelect = (accessToken) => {
  return axios.get(`${base_url}/projects/user-domain-list`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

// ─── postProject ──────────────────────────────────────────────────────────────
//
// The backend is a TWO-STEP process:
//   Step 1 — POST /upload         → uploads file, saves as template.xml on disk
//   Step 2 — POST /save-xml       → reads template.xml from disk, NO file needed
//
// The file was already sent to the server when the user picked it in the seed
// page uploader (handleFileSelect → postSeedPageFile → /upload).
// By the time the user hits Save, template.xml is already on disk.
// Sending the file again to /save-xml does nothing — the controller ignores it.
//
// Geocoder is a completely different flow — it uses /projects/image-project-save
// and does not require a seed page file at all.
//
export const postProject = (accessToken, values) => {
  // Normalize type to lowercase string
  const type =
    typeof values.selectedProjectType === "string"
      ? values.selectedProjectType.toLowerCase()
      : (values.selectedProjectType?.name?.toLowerCase() ?? "wordpress");

  // ── Geocoder — completely separate endpoint and model (WebImages) ───────────
  // Uses /projects/image-project-save which calls imageProjectSave on
  // ProjectController. This uses WebImages model, not WebTemplates, and does
  // not require a seed template file at all.
  if (type === "geocoder") {
    return axios.post(
      `${base_url}/projects/image-project-save`,
      {
        title: values.title,
        notes: values.notes || "",
        google_spread_sheet: values.google_spread_sheet || "",
        script: values.script || "",
        footer_links: values.footer_links || "",
        domain_id: values.domain || "",
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );
  }

  // ── All other types — send metadata only, NO file ─────────────────────────
  // The template file is already on disk from the prior /upload call.
  const formData = new FormData();

  formData.append("type", type);
  if (values.title) formData.append("title", values.title);
  if (values.notes) formData.append("notes", values.notes);
  if (values.google_spread_sheet)
    formData.append("google_spread_sheet", values.google_spread_sheet);
  if (values.script) formData.append("script", values.script);
  if (values.footer_links) formData.append("footer_links", values.footer_links);
  if (values.domain) formData.append("domain_id", values.domain);
  if (values.projectId) formData.append("project_id", values.projectId);

  return axios.post(`${base_url}/save-xml`, formData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "multipart/form-data",
    },
  });
};

export const editProject = (accessToken, values) => {
  return axios.post(
    `${base_url}/projects/update-project`,
    {
      template_name: values.template_name,
      project_id: values.id,
      notes: values.notes,
      google_spread_sheet: values.google_spread_sheet,
      script: values.script,
      footer_links: values.footer_links,
      domain_id: values.domain,
      site_base_url: values.site_base_url,
      type:
        values?.selectedProjectType?.toLowerCase() ||
        values.template_type.toLowerCase(),
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const deleteProject = (accessToken, id, type) => {
  return axios.delete(`${base_url}/projects?project_id=${id}&type=${type}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getEditProjectById = (accessToken, id) => {
  return axios.get(`${base_url}/projects/${id}/edit`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getProjectById = (accessToken, id) => {
  return axios.get(`${base_url}/projects/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

// ─── postTestProject ──────────────────────────────────────────────────────────
//
// Same two-step logic as postProject — template already on disk from /upload.
// Geocoder has no test flow on the backend, so we keep it on save-xml with
// type=geocoder which will gracefully return an error from the backend if
// unsupported, rather than crashing silently.
//
export const postTestProject = (accessToken, values) => {
  console.log("Calling postTestProject with values:", values);

  return axios.post(
    `${base_url}/save-test`,
    {
      project_id: values.project_id || null,
      type: values.type ? values.type.toLowerCase() : "wordpress",
      title: values.title,
      notes: values.notes || "",
      google_spread_sheet: values.google_spread_sheet || "",
      script: values.script || "",
      footer_links: values.footer_links || "",
      domain_id: values.domain_id || "",
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const postConfirmSplitFile = (accessToken, values) => {
  const type =
    typeof values.selectedProjectType === "string"
      ? values.selectedProjectType.toLowerCase()
      : (values.selectedProjectType?.name?.toLowerCase() ??
        values.template_type?.toLowerCase() ??
        "wordpress");

  return axios.post(
    `${base_url}/save-xml`,
    {
      title: values.title,
      project_id: values.id || values.project_id,
      notes: values.notes,
      google_spread_sheet: values.google_spread_sheet,
      script: values.script,
      footer_links: values.footer_links,
      domain_id: values.domain,
      type,
      split_xml_file: values.split_xml_file,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const postLogin = (values) => {
  return axios.post(
    `${base_url}/auth/login`,
    { ...values },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
};

export const postLogOut = (accessToken) => {
  return axios.post(
    `${base_url}/auth/logout`,
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const postPreviewVideoImage = (accessToken, file) => {
  return axios.post(
    `${base_url}/tutorials/upload-preview`,
    { ...file },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const deletePreviewVideoImage = (accessToken) => {
  return axios.delete(`${base_url}/tutorials/delete-preview`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getTutorialLevelList = (accessToken) => {
  return axios.get(`${base_url}/tutorials/level-list`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postCreateTutorial = (accessToken, values) => {
  return axios.post(
    `${base_url}/tutorials`,
    {
      title: values.title,
      description: values.tutorial_description,
      link: values.link,
      difficulty_level: values.difficulty_level,
      preview_name: values.previewImageName,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getTutorialById = (accessToken, id) => {
  return axios.get(`${base_url}/tutorials/${id}/edit`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const putEditTutorial = (accessToken, values, id) => {
  return axios.put(
    `${base_url}/tutorials/${id}`,
    {
      title: values.title,
      description: values.tutorial_description,
      link: values.link,
      difficulty_level: values.difficulty_level,
      preview_name: values.preview_name,
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const deleteTutorialById = (accessToken, id) => {
  return axios.delete(`${base_url}/tutorials/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getMyAgency = (accessToken, query, activeTab) => {
  return axios.get(
    `${base_url}/my-agency?tab=${activeTab}&${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const postMyAgencyInvite = (accessToken, values) => {
  return axios.post(
    `${base_url}/my-agency/invite`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getCheckHasAgency = (accessToken) => {
  return axios.get(`${base_url}/my-agency/check-has-agency`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postMyAgencyAcceptInvite = (accessToken, id) => {
  return axios.post(
    `${base_url}/my-agency/accept-invite`,
    { agency_id: id },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getAgencies = (accessToken, query) => {
  return axios.get(
    `${base_url}/agencies?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const getAgencyUsersList = (accessToken, q) => {
  return axios.get(`${base_url}/agencies/search?q=${q}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postCreateAgency = (accessToken, values) => {
  return axios.post(
    `${base_url}/agencies`,
    {
      name: values.name,
      main_admin_id: Number(values.main_admin_id),
      status: values.status,
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const deleteAgencyById = (accessToken, id) => {
  return axios.delete(`${base_url}/agencies/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const getAgencyById = (accessToken, id) => {
  return axios.get(`${base_url}/agencies/${id}/edit`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const putAgencyEdit = (accessToken, values, id) => {
  return axios.put(
    `${base_url}/agencies/${id}`,
    {
      name: values.name,
      main_admin_id: Number(values.main_admin_id.value),
      status: values.status,
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postResetPassword = (accessToken, values) => {
  return axios.post(`${base_url}/account/password/email`, { ...values });
};

export const postResetPasswordForm = (accessToken, values) => {
  return axios.post(`${base_url}/account/password/reset`, { ...values });
};

export const getStripeProducts = (accessToken) => {
  return axios.get(`${base_url}/stripe/products`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postEditStripeProductField = (accessToken, values) => {
  return axios.post(
    `${base_url}/stripe/products/change-name`,
    {
      name: values.editingField,
      value: values.editValue,
      product_id: String(values.editingRow),
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getProductSubscriptions = (accessToken, query) => {
  return axios.get(
    `${base_url}/stripe/all-subscriptions?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const postCancelSubscription = (accessToken, values) => {
  return axios.post(
    `${base_url}/payment/cancel-subscription`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const getAllUsers = (accessToken, query) => {
  return axios.get(
    `${base_url}/users?${queryString.stringify(query, { arrayFormat: "comma" })}`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
};

export const getAgencyList = (accessToken) => {
  return axios.get(`${base_url}/agencies/list`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postAgencyChange = (accessToken, values) => {
  return axios.post(
    `${base_url}/users/agency-change`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postAgencySwitcherCharge = (accessToken, values) => {
  return axios.post(
    `${base_url}/users/switcher-charge`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postUsersRefreshSubscription = (accessToken, values) => {
  return axios.post(
    `${base_url}/users/refresh-subscription`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postUsersSwitcherIndividualOffer = (accessToken, values) => {
  return axios.post(
    `${base_url}/users/switcher-individual-offer`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postLoginAsUser = (accessToken, values) => {
  return axios.post(
    `${base_url}/users/login-as-user`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postSetPersonalInfo = (accessToken, values) => {
  return axios.post(
    `${base_url}/account/set-personal-info`,
    { ...values },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getAccount = (accessToken) => {
  return axios.get(`${base_url}/account`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const putSetPassword = (accessToken, values) => {
  return axios.put(
    `${base_url}/account/set-password`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postChangeAvatar = (accessToken, values) => {
  return axios.post(
    `${base_url}/account/change-avatar`,
    { avatar: values },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const deleteAvatar = (accessToken) => {
  return axios.delete(`${base_url}/account/delete-avatar`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const cancelProject = (accessToken) => {
  return axios.get(`${base_url}/project/cancel-project`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const postBilling = (accessToken, values) => {
  return axios.post(
    `${base_url}/payment/billing`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};

export const postPaymentSubscribe = (accessToken, values) => {
  return axios.post(
    `${base_url}/payment/subscr`,
    { ...values },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
};
